import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { checkAvailableAdyenAppleAndGoogleMethods, isTest } from '../payment.helpers';
import serializeQueryParams from '../../../utils/url/serialize-query-params';
import axios from '../../../utils/axios';
import API_URLS from '../../../constants/api-urls';
import { addPaymentError, changeState } from '../reducers';
import { ErrorMessageType } from '../components/error-message/error-message';
import { PAYMENT_METHOD_KEYS, PaymentOptionsDataType } from '../payment.constants';

type PaymentOptionsRequestParams = {
    countryCode?: string;
    currencyCode?: string;
    materialId?: string
};

export default function usePaymentOptionsData() {
    const queryParams = serializeQueryParams();
    const dispatch = useDispatch();
    const { t } = useTranslation(['payment']);
    const currency = useSelector((state: RootState) => state.main.currency);
    const { paymentOptionsData, paymentOptionsAvailability } = useSelector((state: RootState) => state.payment);

    const getRequestParams = () => {
        const requestConfig: PaymentOptionsRequestParams = isTest()
            ? {
                countryCode: queryParams.region,
                currencyCode: queryParams.currency
            }
            : { currencyCode: currency.code };

        if (queryParams.materialId) {
            requestConfig.materialId = queryParams.materialId;
        }

        return requestConfig;
    };

    const checkPaymentMethodAvailability = (paymentMethod: PAYMENT_METHOD_KEYS) => {
        switch (paymentMethod) {
            case PAYMENT_METHOD_KEYS.APPLE_PAY:
            case PAYMENT_METHOD_KEYS.GOOGLE_PAY:
                return null;
            default:
                return true;
        }
    };

    useEffect(() => {
        if (!currency.loaded) {
            return;
        }

        axios.get(API_URLS.PAYMENT_OPTIONS, { params: getRequestParams() })
            .then((response) => {
                dispatch(changeState({
                    paymentOptionsData: {
                        ...response,
                        loaded: true
                    }
                }));
            })
            .catch((error: { data: { message: string } }) => {
                if (error?.data?.message !== 'Cart\'s final price is zero') {
                    const errorMessage: ErrorMessageType = {
                        message: t('payment.paymentForm.paypalPlusFetchError')
                    };

                    dispatch(addPaymentError(errorMessage));
                }

                dispatch(changeState({ paymentOptionsData: { loaded: true } as PaymentOptionsDataType }));
            });
    }, [currency.loaded]);

    useEffect(() => {
        if (!paymentOptionsData?.allowedPaymentMethods?.length) {
            return;
        }

        const paymentOptionsAvailability = new Map<PAYMENT_METHOD_KEYS, boolean | null>();

        paymentOptionsData?.allowedPaymentMethods.forEach(paymentMethod => {
            const method = paymentMethod as PAYMENT_METHOD_KEYS;

            paymentOptionsAvailability.set(method, checkPaymentMethodAvailability(method));
        });

        dispatch(changeState({ paymentOptionsAvailability }));

    }, [paymentOptionsData?.allowedPaymentMethods?.length]);

    useEffect(() => {
        const methodsForCheck = Array.from(paymentOptionsAvailability?.keys() ?? [])
            .filter(method => [PAYMENT_METHOD_KEYS.GOOGLE_PAY, PAYMENT_METHOD_KEYS.APPLE_PAY].includes(method) && (paymentOptionsAvailability?.get(method) === null));

        if (methodsForCheck.length) {
            (async () => {
                const googleAndAppleMethodAvailability = await checkAvailableAdyenAppleAndGoogleMethods(methodsForCheck);

                paymentOptionsAvailability?.set(PAYMENT_METHOD_KEYS.GOOGLE_PAY, googleAndAppleMethodAvailability[PAYMENT_METHOD_KEYS.GOOGLE_PAY]);
                paymentOptionsAvailability?.set(PAYMENT_METHOD_KEYS.APPLE_PAY, googleAndAppleMethodAvailability[PAYMENT_METHOD_KEYS.APPLE_PAY]);

                dispatch(changeState({ paymentOptionsAvailability }));
            })();
        }
    }, [paymentOptionsAvailability]);
};
